/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Wed Apr 02 2025
 **/

import { EventEmitter } from "eventemitter3";

const LOGTAG = "#UnityApiController#";

export default class UnityApiController extends EventEmitter {
	private static _instance: UnityApiController | null = null;
	public static get instance() {
		if (!UnityApiController._instance) UnityApiController._instance = new UnityApiController();
		return UnityApiController._instance;
	}

	private _actorId: string = "";
	private _streams: Map<string, MediaStream> = new Map();
	private _feeds: Map<string, MediaStream> = new Map();

	/* Screen Sharing */
	// actorId로 구분
	public get isRunning() {
		return this._streams.get(this._actorId) !== undefined;
	}

	public get actorId() {
		return this._actorId;
	}

	public startScreenSharing(is3DShare: boolean) {
		window.janusPlugin.UnityApi_StartScreenSharing(window.janusPlugin.UniytApi_GetClientId(), is3DShare);
		console.debug(LOGTAG, "Start screen sharing");
	}

	public stopScreenSharing() {
		window.janusPlugin.UnityApi_StopScreenSharing(window.janusPlugin.UniytApi_GetClientId());
		console.debug(LOGTAG, "Stop screen sharing");
	}

	public cancelScreenSharing() {
		console.debug(LOGTAG, "Cancle screen sharing");
	}

	public startLocalScreenStreaming(stream: MediaStream, is3DShare: boolean) {
		this._streams.set(this._actorId, stream);
		this.emit("localStreamUpdated", this._actorId, is3DShare);
		console.debug(LOGTAG, "Start local screen streaming");
	}

	public startRemoteScreenStreaming(stream: MediaStream, peerId: string) {
		if (peerId === this._actorId) return;
		this._streams.set(peerId, stream);
		this.emit("remoteStreamUpdated", peerId, stream);
		console.debug(LOGTAG, "Start remote screen streaming");
	}

	public stopScreenStreaming(actorId: string) {
		this._streams.delete(actorId);
		this.emit("streamClosed", actorId);
		console.debug(LOGTAG, "Stop screen streaming");
	}

	public setActorId(actorId: string) {
		this._actorId = actorId;
	}

	public getStream(id: string) {
		return this._streams.get(id) || null;
	}

	public resetStreams() {
		this._streams.clear();
	}

	/* Camera Feed */
	// userId로 구분
	public onCameraFeedReceived(id: string, stream: MediaStream) {
		if (id.startsWith("loudestSpeakers")) return;
		if (this._feeds.has(id)) return;
		this._feeds.set(id, stream);
		this.emit("cameraFeedReceived", id, stream);
	}

	public onCameraFeedRemoved(id: string) {
		this._feeds.delete(id);
		this.emit("cameraFeedRemoved", id);
	}

	public resetCameraFeeds() {
		this._feeds.clear();
		this.emit("cameraFeedReset");
	}

	public onAudioStateChanged(id: string, isActive: boolean) {
		this.emit("audioStateChanged", id, Boolean(isActive)); // 0과 1을 boolean으로 변환
	}

	public onVideoStateChanged(id: string, isActive: boolean) {
		this.emit("videoStateChanged", id, Boolean(isActive)); // 0과 1을 boolean으로 변환
	}

	public getExistingCameraFeeds() {
		return this._feeds;
	}

	public getClientId() {
		return window.janusPlugin.UniytApi_GetClientId();
	}
}
